import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'antd'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { PlatformType } from '@/types/authorization'
import type { SignupCustomerForm, SignupPricingForm, SignupProductSelection } from '../types'
import {
  clientSignup,
  exchangeBRAccessToken,
  fetchPlatformsByClient,
  markActive,
  refreshPlatform,
  updatePlatformTokens,
} from '../api'
import { getSession, setSession } from '@/lib/session'
import { refreshSessionFromUser, tokenExpiryFromSeconds } from '../session'
import { defaultPricingOptions, normalizeSignupPlatformId, toDatabaseName } from './utils'
import { ADVICEPREP_DEFAULT_CONNECTIONS, type AdvicePrepAppliedCoupon } from '@/features/adviceprep/pricing'
import { createAdvicePrepClient } from '@/features/adviceprep/api'
import { isAdvicePrepClientError } from '@/features/adviceprep/types'
import { clearCachedPermissions } from '@/contexts/AuthorizationContext'
import type { FlowStep } from './SignupContext'

export const STEP_ORDER: FlowStep[] = ['product', 'info', 'pricing', 'payment', 'login', 'importData']
export const ADVICEPREP_SIGNUP_PATH = '/adviceprep-client-signup'
export const ADVICEPREP_HOME_PATH = '/adviceprep'
export const SIGNUP_LANDING_STORAGE_KEY = 'di.signup.landing'
const SIGNUP_WELCOME_STORAGE_KEY = 'di.signup.welcome'
const SIGNUP_CONNECTION_TOAST_STORAGE_KEY = 'di.signup.connectionToast'
const BRWFM_REAUTH_PLATFORM_STORAGE_KEY = 'platformId'

export function useSignupFlow() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [step, setStep] = useState<FlowStep>('product')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const isBasSignup = false
  const isAdvicePrepSignup = location.pathname === ADVICEPREP_SIGNUP_PATH
  const [advicePrepConnections, setAdvicePrepConnections] = useState(ADVICEPREP_DEFAULT_CONNECTIONS)
  const [showAdvicePrepConfirmModal, setShowAdvicePrepConfirmModal] = useState(false)
  const [advicePrepPendingForm, setAdvicePrepPendingForm] = useState<SignupCustomerForm | null>(null)
  const [advicePrepDuplicate, setAdvicePrepDuplicate] = useState(false)
  const [advicePrepStripeCustomerId, setAdvicePrepStripeCustomerId] = useState('')
  const [hideBackButton, setHideBackButton] = useState(false)
  const [nocc, setNocc] = useState(false)
  const [isChildClient, setIsChildClient] = useState(false)
  const [clientSignupCompleted, setClientSignupCompleted] = useState(false)
  const [productSelection, setProductSelection] = useState<SignupProductSelection | null>(null)
  const [customerForm, setCustomerForm] = useState<SignupCustomerForm | null>(null)
  const [pricingForm, setPricingForm] = useState<SignupPricingForm>({ couponCode: '', activeUsers: '', customUsers: 0 })
  const [companyExists, setCompanyExists] = useState(false)
  const [emailExists, setEmailExists] = useState(false)
  const [existingCompanyUser, setExistingCompanyUser] = useState('')
  const [showExistingUserModal, setShowExistingUserModal] = useState(false)
  const [showBasExistsModal, setShowBasExistsModal] = useState(false)
  const [skipPayment, setSkipPayment] = useState(false)
  const [activeUserOptions, setActiveUserOptions] = useState(defaultPricingOptions)
  const [couponValid, setCouponValid] = useState(false)
  const [trialDuration, setTrialDuration] = useState<number | null>(null)
  const [advicePrepCoupon, setAdvicePrepCoupon] = useState<AdvicePrepAppliedCoupon | null>(null)
  const [platformId, setPlatformId] = useState<string>('')
  const [isConnectingData, setIsConnectingData] = useState(false)
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false)
  const popupRef = useRef<Window | null>(null)
  const tokenPollIntervalRef = useRef<number | null>(null)
  const oauthInProcessRef = useRef(false)

  const [productForm] = Form.useForm<{ product: PlatformType }>()
  const [customerInfoForm] = Form.useForm<SignupCustomerForm>()
  const [pricingInfoForm] = Form.useForm<SignupPricingForm>()
  const [loginForm] = Form.useForm<{ email: string; password: string }>()
  const visibleSteps = useMemo<FlowStep[]>(() => {
    if (isAdvicePrepSignup) return ['product', 'info', 'pricing', 'payment', 'success']
    if (nocc && isChildClient) return ['product', 'info', 'login', 'importData']
    if (nocc) return ['product', 'info', 'importData']
    if (isChildClient) return ['product', 'info', 'pricing', 'login', 'importData']
    return ['product', 'info', 'pricing', 'payment', 'importData']
  }, [isAdvicePrepSignup, isChildClient, nocc])

  const runAction = useCallback(async (task: () => Promise<void>, clearInfo = true) => {
    setLoading(true)
    setError(null)
    if (clearInfo) setInfo(null)
    try {
      await task()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  const saveTokens = useCallback(async (email: string, idToken: string, refreshToken: string, expiry: number) => {
    setSession({
      email,
      idToken,
      refreshToken,
      tokenExpiry: tokenExpiryFromSeconds(expiry),
      currentClientId: '',
    })
  }, [])

  const hydrateSessionAndNavigate = useCallback(
    async (email: string) => {
      const nextSession = await refreshSessionFromUser(email)
      setSession(nextSession)
      navigate('/', { replace: true })
    },
    [navigate]
  )

  const establishSignupSession = useCallback(
    async (
      email: string,
      tokens: { idToken: string; refreshToken: string; expiry: number },
      options: { hydrateSession?: boolean; navigateOnSuccess?: boolean } = {}
    ) => {
      const { hydrateSession = false, navigateOnSuccess = false } = options
      await saveTokens(email, tokens.idToken, tokens.refreshToken, tokens.expiry)

      if (hydrateSession || navigateOnSuccess) {
        const nextSession = await refreshSessionFromUser(email)
        setSession(nextSession)

        if (navigateOnSuccess) {
          navigate('/', { replace: true })
        }
      }
    },
    [navigate, saveTokens]
  )

  const finalizeSignup = useCallback(async () => {
    if (!customerForm?.email) {
      navigate('/login')
      return
    }

    setLoading(true)
    try {
      sessionStorage.setItem(
        SIGNUP_WELCOME_STORAGE_KEY,
        JSON.stringify({
          product: productSelection?.product ?? null,
          isBrSignup: productSelection?.product === PlatformType.BRWFM,
        })
      )
      if (isAdvicePrepSignup) {
        sessionStorage.setItem(SIGNUP_LANDING_STORAGE_KEY, ADVICEPREP_HOME_PATH)
      }
      await hydrateSessionAndNavigate(customerForm.email)
    } catch {
      sessionStorage.removeItem(SIGNUP_WELCOME_STORAGE_KEY)
      sessionStorage.removeItem(SIGNUP_LANDING_STORAGE_KEY)
      navigate('/login')
    } finally {
      setLoading(false)
    }
  }, [customerForm?.email, hydrateSessionAndNavigate, isAdvicePrepSignup, navigate, productSelection?.product])

  const ensureActiveAndAuthenticated = useCallback(async (email: string, password: string) => {
    const tokenResponse = await markActive({ email, password, token: '' })
    if (tokenResponse.message === 'SignInRequired') {
      throw new Error('This account signs in with an authenticator app. Please sign in from the login page.')
    }
    if (!tokenResponse.idToken || !tokenResponse.refreshToken || !tokenResponse.expiry) {
      throw new Error('Unable to activate account')
    }
    await establishSignupSession(
      email,
      {
        idToken: tokenResponse.idToken,
        refreshToken: tokenResponse.refreshToken,
        expiry: tokenResponse.expiry,
      }
    )
  }, [establishSignupSession])

  const openAdvicePrepConfirm = useCallback((values: SignupCustomerForm) => {
    setAdvicePrepPendingForm(values)
    setShowAdvicePrepConfirmModal(true)
  }, [])

  /**
   * D-5 — the only thing in the AdvicePrep flow that creates an account.
   * Runs on the modal's affirmative action, never on Next.
   */
  const confirmAdvicePrepAccount = useCallback(async () => {
    const payload = advicePrepPendingForm
    if (!payload) return

    // Back from pricing and confirming again must not create a second client
    // and a second Stripe customer. The account already exists; just move on.
    if (clientSignupCompleted && customerForm?.clientId) {
      setShowAdvicePrepConfirmModal(false)
      setAdvicePrepPendingForm(null)
      setStep('pricing')
      return
    }

    // 5.1c signs this browser in as a brand new user, so drop any permissions
    // cached for whoever was here before rather than letting them pre-fill the
    // authorization provider on the way into the app.
    clearCachedPermissions()

    const response = await createAdvicePrepClient({
      ...payload,
      databaseName: toDatabaseName(payload.companyName),
    })
    if (isAdvicePrepClientError(response)) {
      throw new Error(response.error)
    }

    setCustomerForm({ ...payload, clientId: response.clientId, userId: response.userId })
    setAdvicePrepStripeCustomerId(response.stripeCustomerId)
    setClientSignupCompleted(true)

    // D-13: 5.1c signs the user in, so steps 3 to 5 run authenticated. Absent
    // until the endpoint returns tokens, in which case the wizard carries on
    // unauthenticated rather than failing.
    if (response.session) {
      await establishSignupSession(payload.email, response.session)
    }

    setShowAdvicePrepConfirmModal(false)
    setAdvicePrepPendingForm(null)
    setStep('pricing')
  }, [advicePrepPendingForm, clientSignupCompleted, customerForm?.clientId, establishSignupSession])

  const createAccountWithoutPayment = useCallback(
    async (payload: SignupCustomerForm, child: boolean) => {
      if (!productSelection?.product) throw new Error('Platform not selected')
      const signupPayload = {
        clientForm: {
          ...payload,
          databaseName: toDatabaseName(payload.companyName),
          requireSubscription: false,
          subscribeToBAS: isBasSignup,
        },
        platformType: productSelection.product,
        nocc: true,
      }
      const response = await clientSignup(signupPayload)
      if (response.error || !response.client?.clientId) {
        throw new Error(response.error || 'Unable to create account')
      }
      setClientSignupCompleted(true)
      setPlatformId(normalizeSignupPlatformId(response.platformId))
      setCustomerForm({
        ...payload,
        clientId: response.client.clientId,
        userId: response.userId,
      })
      if (!child) await ensureActiveAndAuthenticated(payload.email, payload.password)
    },
    [
      productSelection?.product,
      setClientSignupCompleted,
      setPlatformId,
      setCustomerForm,
      ensureActiveAndAuthenticated,
    ]
  )

  useEffect(() => {
    const path = location.pathname
    const routeMap: Record<string, PlatformType | undefined> = {
      '/xero-client-signup': PlatformType.Xero,
      '/karbon-client-signup': PlatformType.Karbon,
      '/fyi-client-signup': PlatformType.FYI,
      '/wfm-client-signup': PlatformType.BRWFM,
      '/wfm-client-signup/importData': PlatformType.BRWFM,
    }
    const forcedProduct = routeMap[path]
    const queryPlatform = Number(searchParams.get('platformType'))
    const queryNocc = searchParams.get('nocc')
    const noccValue = !!queryNocc && queryNocc !== 'false'
    setNocc(noccValue)
    if (path === ADVICEPREP_SIGNUP_PATH) {
      setHideBackButton(false)
      setProductSelection({ product: PlatformType.Xero })
      productForm.setFieldsValue({ product: PlatformType.Xero })
      return
    }
    if (forcedProduct) {
      setHideBackButton(false)
      setProductSelection({ product: forcedProduct })
      productForm.setFieldsValue({ product: forcedProduct })
      const initialStep = path === '/wfm-client-signup/importData' ? 'importData' : 'info'
      setStep(initialStep)
      if (path === '/wfm-client-signup/importData') {
        const storedPlatformId = localStorage.getItem(BRWFM_REAUTH_PLATFORM_STORAGE_KEY)
        if (storedPlatformId) {
          setPlatformId(storedPlatformId)
        }
      }
      return
    }
    if (path === '/signup' && Number.isFinite(queryPlatform) && Object.values(PlatformType).includes(queryPlatform)) {
      setHideBackButton(false)
      setProductSelection({ product: queryPlatform as PlatformType })
      productForm.setFieldsValue({ product: queryPlatform as PlatformType })
      setStep('info')
    }
  }, [location.pathname, productForm, searchParams])

  useEffect(() => {
    return () => {
      if (tokenPollIntervalRef.current) {
        window.clearInterval(tokenPollIntervalRef.current)
        tokenPollIntervalRef.current = null
      }
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close()
      popupRef.current = null
    }
  }, [])

  useEffect(() => {
    const oauthCode = searchParams.get('code')
    if (oauthCode && typeof window !== 'undefined' && window.opener) {
      window.opener.postMessage({ code: oauthCode }, window.location.origin)
      window.close()
    }
  }, [searchParams])

  useEffect(() => {
    const completeBrConnection = async (code: string, source: 'message' | 'callback') => {
      if (!code || oauthInProcessRef.current) return
      if (!productSelection?.product || productSelection.product !== PlatformType.BRWFM) return

      const storedPlatformId = typeof window !== 'undefined' ? localStorage.getItem(BRWFM_REAUTH_PLATFORM_STORAGE_KEY) || '' : ''
      const isExistingPlatformReauth = !!storedPlatformId
      const sessionClientId = getSession()?.currentClientId || ''
      const effectivePlatformId = isExistingPlatformReauth ? storedPlatformId : platformId
      const effectiveClientId = isExistingPlatformReauth ? sessionClientId : customerForm?.clientId || ''

      if (!effectiveClientId) {
        setError('Unable to complete data connection because the client context is missing.')
        return
      }

      if (!isExistingPlatformReauth && !customerForm?.clientId) {
        setError('Unable to complete data connection because signup details are missing.')
        return
      }

      oauthInProcessRef.current = true
      try {
        setIsConnectingData(true)
        const token = await exchangeBRAccessToken(code)
        if (!token?.access_token) throw new Error('Unable to exchange BR auth code')
        await updatePlatformTokens({
          accessToken: token.access_token,
          refreshToken: token.refresh_token || '',
          expireIn: token.expires_in || 0,
          platformId: effectivePlatformId || null,
          clientId: effectiveClientId,
        })

        if (effectivePlatformId) {
          await refreshPlatform(effectivePlatformId)
        } else {
          const list = await fetchPlatformsByClient(effectiveClientId)
          const firstId = list?.platforms?.[0]?.platformId
          if (firstId) await refreshPlatform(firstId)
        }

        if (isExistingPlatformReauth) {
          localStorage.removeItem(BRWFM_REAUTH_PLATFORM_STORAGE_KEY)
          setInfo('Data connection completed successfully. You can close this window.')
          if (source === 'callback' && !window.opener) {
            window.close()
          }
          return
        }

        setInfo('Data connection completed successfully.')
        sessionStorage.setItem(
          SIGNUP_CONNECTION_TOAST_STORAGE_KEY,
          JSON.stringify({ message: 'WFM connected successfully' })
        )
        await finalizeSignup()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to complete data connection')
      } finally {
        oauthInProcessRef.current = false
        setIsConnectingData(false)
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close()
        }
        popupRef.current = null
      }
    }

    const listener = async (event: MessageEvent) => {
      const code = (event.data as { code?: string } | undefined)?.code
      await completeBrConnection(code || '', 'message')
    }

    window.addEventListener('message', listener)
    const oauthCode = searchParams.get('code')
    if (oauthCode && !window.opener) {
      void completeBrConnection(oauthCode, 'callback')
    }
    return () => window.removeEventListener('message', listener)
  }, [customerForm?.clientId, finalizeSignup, platformId, popupRef, productSelection?.product, searchParams])

  return useMemo(
    () => ({
      step,
      setStep,
      visibleSteps,
      loading,
      setLoading,
      error,
      setError,
      info,
      setInfo,
      isBasSignup,
      isAdvicePrepSignup,
      advicePrepConnections,
      setAdvicePrepConnections,
      showAdvicePrepConfirmModal,
      setShowAdvicePrepConfirmModal,
      advicePrepPendingForm,
      advicePrepDuplicate,
      setAdvicePrepDuplicate,
      advicePrepStripeCustomerId,
      openAdvicePrepConfirm,
      confirmAdvicePrepAccount,
      hideBackButton,
      nocc,
      isChildClient,
      setIsChildClient,
      clientSignupCompleted,
      setClientSignupCompleted,
      productSelection,
      setProductSelection,
      customerForm,
      setCustomerForm,
      pricingForm,
      setPricingForm,
      companyExists,
      setCompanyExists,
      emailExists,
      setEmailExists,
      existingCompanyUser,
      setExistingCompanyUser,
      showExistingUserModal,
      setShowExistingUserModal,
      showBasExistsModal,
      setShowBasExistsModal,
      skipPayment,
      setSkipPayment,
      activeUserOptions,
      setActiveUserOptions,
      couponValid,
      setCouponValid,
      trialDuration,
      setTrialDuration,
      advicePrepCoupon,
      setAdvicePrepCoupon,
      platformId,
      setPlatformId,
      isConnectingData,
      setIsConnectingData,
      isCheckingConflicts,
      setIsCheckingConflicts,
      productForm,
      customerInfoForm,
      pricingInfoForm,
      loginForm,
      runAction,
      establishSignupSession,
      createAccountWithoutPayment,
      ensureActiveAndAuthenticated,
      saveTokens,
      hydrateSessionAndNavigate,
      finalizeSignup,
      popupRef,
      navigate: (to: string, options?: { replace?: boolean }) => navigate(to, options),
    }),
    [
      step,
      visibleSteps,
      loading,
      error,
      info,
      isAdvicePrepSignup,
      advicePrepConnections,
      showAdvicePrepConfirmModal,
      advicePrepPendingForm,
      advicePrepDuplicate,
      advicePrepStripeCustomerId,
      openAdvicePrepConfirm,
      confirmAdvicePrepAccount,
      hideBackButton,
      nocc,
      isChildClient,
      clientSignupCompleted,
      productSelection,
      customerForm,
      pricingForm,
      companyExists,
      emailExists,
      existingCompanyUser,
      showExistingUserModal,
      showBasExistsModal,
      skipPayment,
      activeUserOptions,
      couponValid,
      trialDuration,
      advicePrepCoupon,
      platformId,
      isConnectingData,
      isCheckingConflicts,
      runAction,
      establishSignupSession,
      createAccountWithoutPayment,
      ensureActiveAndAuthenticated,
      saveTokens,
      hydrateSessionAndNavigate,
      finalizeSignup,
      navigate,
      productForm,
      customerInfoForm,
      pricingInfoForm,
      loginForm,
    ]
  )
}
